/**
 * Privacy policy — sober, scannable, and faithful to the privacy
 * commitment in docs/design.md §6. App Store submission requires this
 * URL; the content here must be accurate to what the shipped app does.
 *
 * Voice rule: calm, no marketing copy. The summary up top is the
 * verbatim sentence from design.md §6 so the marketing language and
 * the policy language never drift apart.
 */

const LAST_UPDATED = "May 24, 2026";

export function Privacy() {
  return (
    <article className="mx-auto max-w-3xl px-6 py-16">
      <header className="mb-10">
        <p className="eyebrow mb-3">Legal</p>
        <h1 className="display mb-3">Privacy policy</h1>
        <p className="text-sm text-[var(--color-text-dim)]">
          Last updated <span className="num">{LAST_UPDATED}</span>
        </p>
      </header>

      <Callout>
        LagSleuth does not transmit data about you, your network, or your activity.
        The app fetches a public list of game-server endpoints once per day from{" "}
        <span className="num text-[var(--color-text-strong)]">
          presets.lagsleuth.latentlab.cc
        </span>
        ; that request carries no identifying information.
      </Callout>

      <Section id="who" title="1. Who this policy is from">
        <p>
          LagSleuth is built and maintained by Thomas Littlejohn under the LatentLab
          name. There is no company, no investors, and no third party with access to
          anything LagSleuth does on your machine.
        </p>
      </Section>

      <Section id="what-we-collect" title="2. What we collect about you">
        <p>
          Nothing. LagSleuth has no accounts, no telemetry, no analytics, no crash
          reporters, no advertising identifiers, and no cookies on this website beyond
          what your browser sends to Cloudflare to fetch the page.
        </p>
        <p className="mt-3">
          The app does not phone home with usage information, session statistics, the
          games you play, the networks you connect from, or the names of servers you
          probe. None of that data leaves your device.
        </p>
      </Section>

      <Section id="the-one-request" title="3. The single recurring network request">
        <p>
          To know which game servers to probe, LagSleuth fetches a small JSON
          manifest from{" "}
          <span className="num">presets.lagsleuth.latentlab.cc/v1/games.json</span>{" "}
          roughly once every twenty-four hours while the app is running.
        </p>
        <p className="mt-3">
          That request contains no user identifier, no information about which games
          you have enabled, and no metrics about your network. The only data exposed
          to our hosting provider (Cloudflare) is the standard metadata any HTTPS
          request carries: your IP address, the timestamp, and your operating
          system's TLS handshake. We do not analyse those logs.
        </p>
        <p className="mt-3">
          The manifest is signed with a public key bundled in the app. If the
          signature does not verify, the app silently falls back to its locally
          cached copy. No user action is required.
        </p>
      </Section>

      <Section id="probes" title="4. The probes LagSleuth runs">
        <p>
          To answer the question {`"is it me or is it the game?"`}, LagSleuth sends
          small UDP and TCP probes from your computer to:
        </p>
        <ul className="mt-3 space-y-1.5 list-disc pl-5 text-[var(--color-text)]">
          <li>
            Public DNS resolvers (<span className="num">1.1.1.1</span>,{" "}
            <span className="num">8.8.8.8</span>,{" "}
            <span className="num">9.9.9.9</span>) as baseline latency checks.
          </li>
          <li>
            The game-server endpoints listed in the manifest above, only for games
            you have enabled.
          </li>
          <li>
            Public STUN servers, only when the diagnostic NAT-detection test is run
            (manually triggered by you).
          </li>
        </ul>
        <p className="mt-3">
          These are normal network probes that any traffic on your network would
          generate. They contain no identifying payload. We do not transmit the
          results of these probes anywhere — they are computed on your device and
          displayed back to you.
        </p>
      </Section>

      <Section id="device-permissions" title="5. Permissions LagSleuth requests">
        <p>The app may ask your operating system for:</p>
        <ul className="mt-3 space-y-1.5 list-disc pl-5 text-[var(--color-text)]">
          <li>
            <strong className="text-[var(--color-text-strong)]">Notifications</strong>{" "}
            — so Sleuth can tell you when something is wrong without you having to
            check.
          </li>
          <li>
            <strong className="text-[var(--color-text-strong)]">Location</strong>{" "}
            (macOS only) — required by Apple to read the current Wi-Fi SSID and
            signal strength, which are otherwise hidden from sandboxed apps. We do
            not derive or store your geographic location.
          </li>
          <li>
            <strong className="text-[var(--color-text-strong)]">Network</strong> —
            outbound network access to run the probes described above. LagSleuth
            never opens listening sockets and never modifies your system network
            settings.
          </li>
        </ul>
        <p className="mt-3">
          Each permission is optional. If you decline, the corresponding feature is
          disabled with an explanation in the app; the rest of LagSleuth continues
          to work.
        </p>
      </Section>

      <Section id="local-storage" title="6. What we store on your device">
        <p>
          LagSleuth keeps a local SQLite database under your app container — typical
          path on macOS:{" "}
          <span className="num text-xs">
            ~/Library/Containers/cc.latentlab.lagsleuth/Data/
          </span>
          . The database holds your probe history (RTT samples, loss percentages,
          jitter, session summaries) and your settings. The default retention is
          thirty days; older rows are deleted automatically.
        </p>
        <p className="mt-3">
          To delete this data immediately, choose Settings → Clear all data in the
          app, or simply uninstall it.
        </p>
      </Section>

      <Section id="purchases" title="7. Purchases">
        <p>
          LagSleuth is sold through the Mac App Store. The purchase, refund, and
          subscription handling is entirely Apple's; we receive only an aggregate
          monthly payout and never see your name, billing address, or payment
          method.{" "}
          <a
            href="https://www.apple.com/legal/privacy/"
            rel="noreferrer noopener"
          >
            Apple's privacy policy
          </a>{" "}
          covers that transaction.
        </p>
      </Section>

      <Section id="third-parties" title="8. Third parties">
        <p>
          We use Cloudflare to host{" "}
          <span className="num">presets.lagsleuth.latentlab.cc</span> and this
          website. Cloudflare's standard request logs apply. We do not use any
          analytics service, advertising network, A/B-testing platform, or
          customer-data platform.
        </p>
        <p className="mt-3">
          We do not share, sell, or transfer any information — because we do not
          have any to share.
        </p>
      </Section>

      <Section id="children" title="9. Children">
        <p>
          LagSleuth is rated 4+ in the App Store and is suitable for users of any
          age. We collect no information from anyone, including children.
        </p>
      </Section>

      <Section id="your-rights" title="10. Your rights under GDPR, CCPA, and similar">
        <p>
          Regulations like the EU's GDPR and California's CCPA give you the right to
          access, correct, or delete personal data that a service holds about you.
          Because LagSleuth holds no personal data about you on any of our servers,
          there is nothing for us to access, correct, or delete on your behalf. The
          data on your own device is yours; the Clear all data action in Settings
          erases it.
        </p>
        <p className="mt-3">
          If you believe we have somehow collected information about you in error,
          contact us at the email below and we will investigate.
        </p>
      </Section>

      <Section id="changes" title="11. Changes to this policy">
        <p>
          If we change this policy, we will update the Last updated date at the top.
          Material changes — anything that expands what data the app sends — will
          also be announced in the app itself before the change takes effect.
        </p>
      </Section>

      <Section id="contact" title="12. Contact">
        <p>
          Questions about this policy or about LagSleuth's privacy posture:{" "}
          <a href="mailto:privacy@latentlab.cc">privacy@latentlab.cc</a>.
        </p>
      </Section>

      <footer className="mt-16 pt-8 border-t border-[var(--color-border-soft)] text-xs text-[var(--color-text-dim)]">
        This policy is written in plain English on purpose. If anything is unclear,
        please tell us — that's a bug.
      </footer>
    </article>
  );
}

function Callout({ children }: { children: React.ReactNode }) {
  return (
    <aside className="mb-10 rounded-xl border border-[var(--color-accent-line)] bg-[var(--color-accent-soft)] p-5 text-sm leading-relaxed text-[var(--color-text)]">
      {children}
    </aside>
  );
}

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mb-10 scroll-mt-20">
      <h2 className="h2 mb-3">{title}</h2>
      <div className="text-sm text-[var(--color-text-muted)] leading-relaxed">
        {children}
      </div>
    </section>
  );
}
